/**
 * Passive media diagnostics.
 *
 * Logs (via the persistent logger, see ./persistentLog) everything needed to
 * understand why a camera/microphone permission prompt did or did not appear
 * on a given device:
 * - environment: iframe embedding, ancestor origins, secure context
 * - Permissions Policy verdict for camera/microphone (iframe `allow` attribute)
 * - Permissions API state for camera/microphone (+ changes)
 * - every getUserMedia call: constraints, duration, resulting tracks or error
 *   name (a long duration means a prompt was shown; an instant failure means
 *   it was not)
 * - every enumerateDevices result change (empty videoinput list = the silent
 *   no-op path in MediaCapture)
 *
 * Strictly observational: wrapped APIs re-throw and return untouched results.
 *
 * Gated by the `logs.mediaDiagnostics` setting (settings UI toggle, imported
 * JSON config, SETTINGS env var or `?settings=` URL overlay). Disabled by
 * default; the gate reacts to settings changes at runtime.
 *
 * The logger object is exposed globally as `__mediaDiag`: run
 * `__mediaDiag.dumpConsole()` in the console to print the whole history — the
 * buffer survives reloads via localStorage, so it can be read after the
 * incident. `__mediaDiag.setEnabled(true)` force-enables logging for the
 * current page (unlike the settings gate, it does not log the startup
 * snapshot).
 */

import { createPersistentLogger } from "./persistentLog";
import { getResolvedSettings, subscribeSettings } from "./settings";

const PREFIX = "[media-diag]";
const DEFAULT_ENABLED = false;

let initialized = false;

export const diagLogger = createPersistentLogger({
  prefix: PREFIX,
  storageKey: "kalyzee:media-diag",
  enabled: DEFAULT_ENABLED,
});

(window as any).__mediaDiag = diagLogger;

const readEnabledFromSettings = (): boolean => getResolvedSettings().logs?.mediaDiagnostics ?? DEFAULT_ENABLED;

const summarizeDevice = (d: MediaDeviceInfo) => ({
  kind: d.kind,
  id: d.deviceId ? `${d.deviceId.slice(0, 8)}…` : "(empty)",
  group: d.groupId ? `${d.groupId.slice(0, 8)}…` : "(empty)",
  label: d.label || "(empty)",
});

const logEnvironment = () => {
  let embedded = false;
  try {
    embedded = window.self !== window.top;
  } catch (_) {
    // Cross-origin access to window.top throws: definitely embedded.
    embedded = true;
  }
  let ancestorOrigins: string[] | undefined;
  try {
    if (window.location.ancestorOrigins) ancestorOrigins = Array.from(window.location.ancestorOrigins);
  } catch (_) {}
  diagLogger.log("env", {
    url: window.location.origin + window.location.pathname,
    embeddedInIframe: embedded,
    ancestorOrigins,
    referrer: document.referrer || "(none)",
    isSecureContext: window.isSecureContext,
    hasMediaDevices: !!navigator.mediaDevices,
    hasGetUserMedia: !!navigator.mediaDevices?.getUserMedia,
    userAgent: navigator.userAgent,
  });
};

const logPermissionsPolicy = () => {
  // `featurePolicy` (Chrome) / `permissionsPolicy` are not in lib.dom typings.
  const policy = (document as any).permissionsPolicy ?? (document as any).featurePolicy;
  if (!policy?.allowsFeature) {
    diagLogger.log("permissionsPolicy", "API not available (Safari/Firefox)");
    return;
  }
  try {
    diagLogger.log("permissionsPolicy", {
      camera: policy.allowsFeature("camera"),
      microphone: policy.allowsFeature("microphone"),
      displayCapture: policy.allowsFeature("display-capture"),
    });
  } catch (err) {
    diagLogger.log("permissionsPolicy", `allowsFeature failed: ${String(err)}`);
  }
};

const watchPermission = async (name: "camera" | "microphone") => {
  if (!navigator.permissions?.query) {
    diagLogger.log(`permission ${name}`, "Permissions API not available");
    return;
  }
  try {
    const status = await navigator.permissions.query({ name: name as PermissionName });
    diagLogger.log(`permission ${name}`, status.state);
    status.onchange = () => diagLogger.log(`permission ${name} changed`, status.state);
  } catch (err) {
    // Safari < 16 does not support these names.
    diagLogger.log(`permission ${name}`, `query failed: ${String(err)}`);
  }
};

const wrapMediaDevices = () => {
  const md = navigator.mediaDevices as (MediaDevices & { __mediaDiagWrapped?: boolean }) | undefined;
  if (!md || md.__mediaDiagWrapped) return;
  md.__mediaDiagWrapped = true;

  const originalGum = md.getUserMedia?.bind(md);
  if (originalGum) {
    let counter = 0;
    md.getUserMedia = async (constraints?: MediaStreamConstraints) => {
      counter += 1;
      const id = counter;
      const started = performance.now();
      diagLogger.log(`getUserMedia #${id} request`, constraints);
      try {
        const stream = await originalGum(constraints);
        diagLogger.log(
          `getUserMedia #${id} success in ${Math.round(performance.now() - started)}ms`,
          stream.getTracks().map((t) => ({ kind: t.kind, label: t.label, readyState: t.readyState })),
        );
        return stream;
      } catch (err: any) {
        diagLogger.log(`getUserMedia #${id} FAILED in ${Math.round(performance.now() - started)}ms`, {
          name: err?.name,
          message: err?.message ?? String(err),
          constraint: err?.constraint,
        });
        throw err;
      }
    };
  }

  const originalEnumerate = md.enumerateDevices?.bind(md);
  if (originalEnumerate) {
    let lastSummary = "";
    md.enumerateDevices = async () => {
      const devices = await originalEnumerate();
      const summary = devices.map(summarizeDevice);
      const json = JSON.stringify(summary);
      // Called very often (every devicechange, every hook mount): only log changes.
      if (json !== lastSummary) {
        lastSummary = json;
        diagLogger.log("enumerateDevices changed", {
          videoinput: devices.filter((d) => d.kind === "videoinput").length,
          audioinput: devices.filter((d) => d.kind === "audioinput").length,
          audiooutput: devices.filter((d) => d.kind === "audiooutput").length,
          devices: summary,
        });
      }
      return devices;
    };
  }

  md.addEventListener?.("devicechange", () => diagLogger.log("devicechange event"));
};

const logStartupSnapshot = () => {
  logEnvironment();
  logPermissionsPolicy();
  watchPermission("camera");
  watchPermission("microphone");
  // First snapshot of the device list, through the wrapper.
  navigator.mediaDevices?.enumerateDevices?.().catch((err) => diagLogger.log("enumerateDevices failed", String(err)));
};

const applyEnabled = (enabled: boolean, reason: "init" | "update") => {
  if (reason !== "init" && enabled === diagLogger.isEnabled()) return;
  diagLogger.setEnabled(enabled);
  if (enabled) logStartupSnapshot();
};

export const initMediaDiagnostics = () => {
  if (initialized) return;
  initialized = true;

  // Always wrap, even while disabled: a runtime enable (settings change,
  // __mediaDiag.setEnabled) must already observe the calls.
  wrapMediaDevices();

  applyEnabled(readEnabledFromSettings(), "init");
  subscribeSettings(() => applyEnabled(readEnabledFromSettings(), "update"));
};
